import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { getDashboardSummary } from '../../../shared/services/dashboardService';
import { DashboardData } from '../../../shared/types/dashboard';

const formatCurrency = (value: number | undefined | null) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value ?? 0);

const statusColor: Record<string, string> = {
    PLACED: 'bg-blue-100 text-blue-700',
    CONFIRMED: 'bg-indigo-100 text-indigo-700',
    ASSIGNED: 'bg-purple-100 text-purple-700',
    PICKED_UP: 'bg-yellow-100 text-yellow-700',
    OUT_FOR_DELIVERY: 'bg-orange-100 text-orange-700',
    DELIVERED: 'bg-green-100 text-green-700',
    CANCELLED: 'bg-red-100 text-red-700',
};

interface StatCardProps {
    title: string;
    value: string | number;
    sub?: string;
    icon: string;
    iconBg: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, sub, icon, iconBg }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
        <div className={`flex items-center justify-center w-12 h-12 rounded-full text-2xl ${iconBg}`}>
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
    </div>
);

export const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await getDashboardSummary();
                if (res.success && res.data) {
                    setData(res.data);
                } else {
                    setError('Failed to load dashboard data.');
                }
            } catch {
                setError('Something went wrong. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Build chart data
    const chartCategories = data?.chart.last7Days.map((d) => d._id) ?? [];
    const chartRevenueData = data?.chart.last7Days.map((d) => d.revenue) ?? [];
    const chartOrdersData = data?.chart.last7Days.map((d) => d.orders) ?? [];

    const chartOptions: ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        dataLabels: { enabled: false },
        xaxis: {
            categories: chartCategories,
            labels: {
                formatter: (val: string) => {
                    const d = new Date(val);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                },
            },
        },
        yaxis: [
            {
                title: { text: 'Revenue (₹)' },
                labels: { formatter: (val: number) => `₹${(val / 1000).toFixed(0)}k` },
            },
            {
                opposite: true,
                title: { text: 'Orders' },
                labels: { formatter: (val: number) => `${val}` },
            },
        ],
        colors: ['#6366f1', '#f59e0b'],
        legend: { position: 'top' },
        tooltip: {
            y: [
                { formatter: (val: number) => formatCurrency(val) },
                { formatter: (val: number) => `${val} orders` },
            ],
        },
        grid: { borderColor: '#f1f5f9' },
    };

    const chartSeries = [
        { name: 'Revenue', type: 'bar', data: chartRevenueData },
        { name: 'Orders', type: 'line', data: chartOrdersData },
    ];

    return (
        <div className="flex flex-col w-full min-h-full bg-gray-50">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex-1 min-w-0">
                    <div className="mt-2">
                        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-7 sm:leading-10 truncate text-gray-900">
                            Dashboard
                        </h2>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-1 p-6 sm:p-8 overflow-y-auto">
                {loading && (
                    <div className="flex items-center justify-center w-full">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                    </div>
                )}

                {error && !loading && (
                    <div className="w-full flex items-center justify-center">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">{error}</div>
                    </div>
                )}

                {!loading && !error && data && (
                    <div className="flex flex-col w-full gap-6">

                        {/* GoFrugal Error Logs Alert */}
                        {data.goFrugalErrors.total > 0 && (
                            <button
                                type="button"
                                onClick={() => navigate('/gofrugal-error-logs')}
                                className="w-full flex items-center justify-between gap-4 bg-red-50 border border-red-200 rounded-xl shadow-sm p-4 hover:bg-red-100 transition-colors text-left"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-full text-2xl bg-red-100">
                                        ⚠️
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-red-800">
                                            {data.goFrugalErrors.total} GoFrugal sync {data.goFrugalErrors.total === 1 ? 'error' : 'errors'} logged
                                        </p>
                                        <p className="text-xs text-red-600 mt-0.5">Click to view and manage error logs</p>
                                    </div>
                                </div>
                                <span className="text-red-700 text-sm font-semibold whitespace-nowrap">View Logs →</span>
                            </button>
                        )}

                        {/* Row 1 — Orders & Revenue */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                            <StatCard
                                title="Total Orders"
                                value={data.orders.total?.toLocaleString() || '0'}
                                sub={`Today: ${data.orders.today} · Week: ${data.orders.thisWeek}`}
                                icon="🛒"
                                iconBg="bg-indigo-50"
                            />
                            <StatCard
                                title="Total Revenue"
                                value={formatCurrency(data.revenue.total)}
                                sub={`Today: ${formatCurrency(data.revenue.today)}`}
                                icon="💰"
                                iconBg="bg-green-50"
                            />
                            <StatCard
                                title="Total Customers"
                                value={data.customers.total?.toLocaleString() || '0'}
                                sub={`Active: ${data.customers.active} · New this month: ${data.customers.newThisMonth}`}
                                icon="👥"
                                iconBg="bg-blue-50"
                            />
                            <StatCard
                                title="Total Products"
                                value={data.products.total?.toLocaleString() || '0'}
                                sub={`Active: ${data.products.active} · Out of stock: ${data.products.outOfStock}`}
                                icon="📦"
                                iconBg="bg-orange-50"
                            />
                        </div>

                        {/* Row 2 — This Month */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                            <StatCard
                                title="This Month Orders"
                                value={data.orders.thisMonth?.toLocaleString() || '0'}
                                icon="📋"
                                iconBg="bg-purple-50"
                            />
                            <StatCard
                                title="This Month Revenue"
                                value={formatCurrency(data.revenue.thisMonth)}
                                icon="📈"
                                iconBg="bg-teal-50"
                            />
                            <StatCard
                                title="This Week Revenue"
                                value={formatCurrency(data.revenue.thisWeek)}
                                icon="📅"
                                iconBg="bg-yellow-50"
                            />
                            <StatCard
                                title="Inactive Products"
                                value={data.products.inactive?.toLocaleString() || '0'}
                                sub={`Out of stock: ${data.products.outOfStock}`}
                                icon="⚠️"
                                iconBg="bg-red-50"
                            />
                        </div>

                        {/* Row 3 — Chart + Order Status */}
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                            {/* Revenue Chart */}
                            <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                                <h3 className="text-base font-semibold text-gray-900 mb-4">Last 7 Days — Revenue & Orders</h3>
                                {chartCategories.length > 0 ? (
                                    <ReactApexChart
                                        options={chartOptions}
                                        series={chartSeries}
                                        type="bar"
                                        height={280}
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No chart data available.</div>
                                )}
                            </div>

                            {/* Order Status Breakdown */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                                <h3 className="text-base font-semibold text-gray-900 mb-4">Orders by Status</h3>
                                <div className="flex flex-col gap-3">
                                    {Object.entries(data.orders.byStatus).map(([status, count]) => (
                                        <div key={status} className="flex items-center justify-between">
                                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor[status] ?? 'bg-gray-100 text-gray-600'}`}>
                                                {status.replace(/_/g, ' ')}
                                            </span>
                                            <span className="text-sm font-bold text-gray-700">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Row 4 — Recent Orders */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                            <h3 className="text-base font-semibold text-gray-900 mb-4">Recent Orders</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-100">
                                            <th className="text-left pb-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Order #</th>
                                            <th className="text-left pb-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Customer</th>
                                            <th className="text-left pb-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Status</th>
                                            <th className="text-left pb-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Payment</th>
                                            <th className="text-right pb-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Amount</th>
                                            <th className="text-right pb-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {data.recentOrders.map((order, index) => (
                                            <tr
                                                key={order._id || order.orderNumber || `${order.placedAt}-${index}`}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="py-3 font-medium text-indigo-600">#{order.orderNumber}</td>
                                                <td className="py-3 text-gray-700">{order.address?.receiverName ?? '—'}</td>
                                                <td className="py-3">
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor[order.orderStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                                                        {order.orderStatus.replace(/_/g, ' ')}
                                                    </span>
                                                </td>
                                                <td className="py-3 text-gray-600">{order.paymentMethod}</td>
                                                <td className="py-3 text-right font-semibold text-gray-900">{formatCurrency(order.grandTotal)}</td>
                                                <td className="py-3 text-right text-gray-400">
                                                    {new Date(order.placedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </td>
                                            </tr>
                                        ))}
                                        {data.recentOrders.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="py-8 text-center text-gray-400">No recent orders.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};
